import React, { useState, useMemo } from 'react';
import { Table, Card, DatePicker, Button, message, Select, Tabs, Row, Col, Statistic } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { SearchOutlined, DownloadOutlined, FileTextOutlined, ShoppingOutlined, DollarOutlined, BarChartOutlined, DatabaseOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { theme } from '../../../theme';

const { RangePicker } = DatePicker;

interface ConsumoInternoItem {
  fecha: string;
  nro_comprobante: string;
  insumo: string;
  sector: string;
  equipo: string;
  motor: string;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

type TipoPrecio = 'promedio' | 'ultimo' | 'mayor';

const ConsumoInterno: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<ConsumoInternoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [tipoPrecio, setTipoPrecio] = useState<TipoPrecio>('promedio');
  const [filteredData, setFilteredData] = useState<ConsumoInternoItem[]>([]);
  const [filtroInsumo, setFiltroInsumo] = useState<string>('todos');
  const [filtroSector, setFiltroSector] = useState<string>('todos');

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  const columns: ColumnsType<ConsumoInternoItem> = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (text) => dayjs(text).format('DD/MM/YYYY'),
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.fecha))).map(fecha => ({
        text: dayjs(fecha).format('DD/MM/YYYY'),
        value: fecha,
      })),
      onFilter: (value, record) => record.fecha === value,
    },
    {
      title: 'Comprobante',
      dataIndex: 'nro_comprobante',
      key: 'nro_comprobante',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.nro_comprobante))).map(comp => ({
        text: comp,
        value: comp,
      })),
      onFilter: (value, record) => record.nro_comprobante === value,
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo',
      key: 'insumo',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.insumo))).map(insumo => ({
        text: insumo,
        value: insumo,
      })),
      onFilter: (value, record) => record.insumo === value,
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.sector))).map(sector => ({
        text: sector,
        value: sector,
      })),
      onFilter: (value, record) => record.sector === value,
    },
    {
      title: 'Equipo',
      dataIndex: 'equipo',
      key: 'equipo',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.equipo))).map(equipo => ({
        text: equipo,
        value: equipo,
      })),
      onFilter: (value, record) => record.equipo === value,
    },
    {
      title: 'Motor',
      dataIndex: 'motor',
      key: 'motor',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.motor))).map(motor => ({
        text: motor,
        value: motor,
      })),
      onFilter: (value, record) => record.motor === value,
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right',
    },
    {
      title: 'Precio Unit.',
      dataIndex: 'precio_unitario',
      key: 'precio_unitario',
      align: 'right',
      render: (value) => value.toFixed(2),
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      align: 'right',
      render: (value) => value.toFixed(2),
    },
  ];

  const buscarConsumos = async () => {
    if (!empresa || !sucursal) {
      message.error('Faltan datos necesarios para la consulta');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const fechaDesde = fechas[0].format('YYYY-MM-DD');
      const fechaHasta = fechas[1].format('YYYY-MM-DD');
      
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/consumos_internos/${empresa}/${sucursal}/${fechaDesde}/${fechaHasta}/${tipoPrecio}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos');
      }

      const responseData = await response.json() as ConsumoInternoItem[];
      setData(responseData);
      message.success('Consumos internos obtenidos correctamente');
      
    } catch (error) {
      message.error('Error al obtener los consumos internos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (currentData: readonly ConsumoInternoItem[]) => {
    return currentData.reduce((acc, item) => acc + item.subtotal, 0);
  };

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    // Filtrar datos según filtros seleccionados
    let datosFiltrados = data;
    if (filtroInsumo !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.insumo === filtroInsumo);
    }
    if (filtroSector !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.sector === filtroSector);
    }

    const totalRegistros = datosFiltrados.length;
    const totalCantidad = datosFiltrados.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    const totalSubtotal = datosFiltrados.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const promedioSubtotal = totalRegistros > 0 ? totalSubtotal / totalRegistros : 0;
    const promedioCantidad = totalRegistros > 0 ? totalCantidad / totalRegistros : 0;
    
    // Distribución por insumo
    const porInsumo = datosFiltrados.reduce((acc, item) => {
      const insumo = item.insumo || 'Sin insumo';
      if (!acc[insumo]) {
        acc[insumo] = { cantidad: 0, subtotal: 0 };
      }
      acc[insumo].cantidad += item.cantidad || 0;
      acc[insumo].subtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; subtotal: number }>);
    
    const distribucionInsumo = Object.entries(porInsumo)
      .map(([insumo, datos]) => ({
        insumo,
        cantidad: datos.cantidad,
        subtotal: datos.subtotal,
        porcentaje: totalSubtotal > 0 ? (datos.subtotal / totalSubtotal) * 100 : 0
      }))
      .sort((a, b) => b.subtotal - a.subtotal)
      .slice(0, 10);
    
    // Distribución por sector
    const porSector = datosFiltrados.reduce((acc, item) => {
      const sector = item.sector || 'Sin sector';
      if (!acc[sector]) {
        acc[sector] = { cantidad: 0, subtotal: 0 };
      }
      acc[sector].cantidad += item.cantidad || 0;
      acc[sector].subtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; subtotal: number }>);
    
    const distribucionSector = Object.entries(porSector)
      .map(([sector, datos]) => ({
        sector,
        cantidad: datos.cantidad,
        subtotal: datos.subtotal,
        porcentaje: totalSubtotal > 0 ? (datos.subtotal / totalSubtotal) * 100 : 0
      }))
      .sort((a, b) => b.subtotal - a.subtotal)
      .slice(0, 10);
    
    // Distribución por día
    const porDia = datosFiltrados.reduce((acc, item) => {
      const dia = item.fecha || 'Sin fecha';
      if (!acc[dia]) {
        acc[dia] = { cantidad: 0, subtotal: 0 };
      }
      acc[dia].cantidad += item.cantidad || 0;
      acc[dia].subtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; subtotal: number }>);
    
    const distribucionDia = Object.entries(porDia)
      .map(([dia, datos]) => ({
        dia,
        cantidad: datos.cantidad,
        subtotal: datos.subtotal
      }))
      .sort((a, b) => a.dia.localeCompare(b.dia))
      .slice(0, 30); // Últimos 30 días
    
    const insumosUnicos = Array.from(new Set(data.map(item => item.insumo))).sort();
    const sectoresUnicos = Array.from(new Set(data.map(item => item.sector))).sort();
    
    return {
      totalRegistros,
      totalCantidad,
      totalSubtotal,
      promedioSubtotal,
      promedioCantidad,
      distribucionInsumo,
      distribucionSector,
      distribucionDia,
      insumosUnicos,
      sectoresUnicos
    };
  }, [data, filtroInsumo, filtroSector]);

  const exportToExcel = () => {
    // Preparar los datos para Excel
    const excelData = data.map(item => ({
      'Fecha': dayjs(item.fecha).format('DD/MM/YYYY'),
      'Comprobante': item.nro_comprobante,
      'Insumo': item.insumo,
      'Sector': item.sector,
      'Equipo': item.equipo,
      'Motor': item.motor,
      'Cantidad': Number(item.cantidad),
      'Precio Unit.': Number(item.precio_unitario),
      'Subtotal': Number(item.subtotal)
    }));

    // Agregar fila de total
    const total = calculateTotal(filteredData.length > 0 ? filteredData : data);
    excelData.push({
      'Fecha': '',
      'Comprobante': '',
      'Insumo': '',
      'Sector': '',
      'Equipo': '',
      'Motor': 'Total General:',
      'Cantidad': null,
      'Precio Unit.': null,
      'Subtotal': Number(total)
    } as any); // Usamos 'as any' para evitar el error de tipos

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Configurar formato de números para las columnas numéricas
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let C = range.s.c; C <= range.e.c; C++) {
      for (let R = range.s.r; R <= range.e.r; R++) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && typeof cell.v === 'number') {
          cell.z = '#,##0.00'; // Formato de número con 2 decimales
        }
      }
    }

    // Agregar worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Consumos Internos');

    // Guardar archivo
    XLSX.writeFile(wb, `Consumos_Internos_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Consumo Interno
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Consumos Internos"
      >
        <div style={{ padding: '24px' }}>
          {/* Filtros */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '24px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Rango de Fechas
              </label>
              <RangePicker
                value={fechas}
                onChange={(dates) => dates && setFechas([dates[0]!, dates[1]!])}
                format="DD/MM/YYYY"
                size="large"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Tipo de Precio
              </label>
              <Select
                value={tipoPrecio}
                onChange={setTipoPrecio}
                size="large"
                style={{ width: '100%' }}
                options={[
                  { label: 'Precio Promedio', value: 'promedio' },
                  { label: 'Último Precio', value: 'ultimo' },
                  { label: 'Precio Mayor', value: 'mayor' },
                ]}
              />
            </div>
            <div>
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={buscarConsumos}
                loading={loading}
                size="small"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Buscar
              </Button>
            </div>
            <div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={data.length === 0}
                size="small"
                style={{
                  fontWeight: 500,
                  width: '100%',
                  opacity: data.length === 0 ? 0.6 : 1
                }}
              >
                Exportar Excel
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            defaultActiveKey="listado"
            size="small"
            items={[
              {
                key: 'listado',
                label: 'Listado',
                children: (
                  <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    scroll={{ x: 1200 }}
                    pagination={{
                      pageSize: 6,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`,
                    }}
                    onChange={(pagination, filters, sorter, extra) => {
                      setFilteredData(extra.currentDataSource as ConsumoInternoItem[]);
                    }}
                    summary={() => {
                      const total = calculateTotal(filteredData.length > 0 ? filteredData : data);
                      return (
                        <Table.Summary fixed>
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={8} align="right">
                              <strong>Total General:</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              <strong>{total.toFixed(2)}</strong>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      );
                    }}
                  />
                )
              },
              {
                key: 'estadisticas',
                label: 'Estadísticas',
                children: (
                  <div style={{ padding: '20px 0' }}>
                    {/* Filtros */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Insumo:</div>
                        <Select
                          value={filtroInsumo}
                          onChange={setFiltroInsumo}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todos los insumos"
                        >
                          <Select.Option value="todos">Todos</Select.Option>
                          {estadisticas.insumosUnicos.map(ins => (
                            <Select.Option key={ins} value={ins}>{ins}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Sector:</div>
                        <Select
                          value={filtroSector}
                          onChange={setFiltroSector}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todos los sectores"
                        >
                          <Select.Option value="todos">Todos</Select.Option>
                          {estadisticas.sectoresUnicos.map(sec => (
                            <Select.Option key={sec} value={sec}>{sec}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>

                    {/* Tarjetas de resumen */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total de Registros"
                            value={estadisticas.totalRegistros}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: theme.colors.primary }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Cantidad"
                            value={estadisticas.totalCantidad}
                            prefix={<ShoppingOutlined />}
                            valueStyle={{ color: theme.colors.success }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Subtotal"
                            value={estadisticas.totalSubtotal}
                            prefix={<DollarOutlined />}
                            precision={2}
                            valueStyle={{ color: theme.colors.info }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Promedio Subtotal"
                            value={estadisticas.promedioSubtotal}
                            prefix={<BarChartOutlined />}
                            precision={2}
                            valueStyle={{ color: theme.colors.warning }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Promedio Cantidad"
                            value={estadisticas.promedioCantidad.toFixed(2)}
                            prefix={<DatabaseOutlined />}
                            valueStyle={{ color: theme.colors.primary }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Distribución por Insumo */}
                    <Card 
                      title="Top 10 - Distribución por Insumo" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionInsumo}
                        rowKey="insumo"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Insumo',
                            dataIndex: 'insumo',
                            key: 'insumo',
                          },
                          {
                            title: 'Cantidad',
                            dataIndex: 'cantidad',
                            key: 'cantidad',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Subtotal',
                            dataIndex: 'subtotal',
                            key: 'subtotal',
                            align: 'right' as const,
                            render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                          },
                          {
                            title: 'Porcentaje',
                            dataIndex: 'porcentaje',
                            key: 'porcentaje',
                            align: 'right' as const,
                            render: (valor: number) => `${valor.toFixed(1)}%`,
                          },
                        ]}
                      />
                    </Card>

                    {/* Distribución por Sector */}
                    <Card 
                      title="Top 10 - Distribución por Sector" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionSector}
                        rowKey="sector"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Sector',
                            dataIndex: 'sector',
                            key: 'sector',
                          },
                          {
                            title: 'Cantidad',
                            dataIndex: 'cantidad',
                            key: 'cantidad',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Subtotal',
                            dataIndex: 'subtotal',
                            key: 'subtotal',
                            align: 'right' as const,
                            render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                          },
                          {
                            title: 'Porcentaje',
                            dataIndex: 'porcentaje',
                            key: 'porcentaje',
                            align: 'right' as const,
                            render: (valor: number) => `${valor.toFixed(1)}%`,
                          },
                        ]}
                      />
                    </Card>

                    {/* Distribución por Día */}
                    <Card 
                      title="Últimos 30 Días - Consumos por Día" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionDia}
                        rowKey="dia"
                        pagination={false}
                        size="small"
                        scroll={{ x: 600 }}
                        columns={[
                          {
                            title: 'Fecha',
                            dataIndex: 'dia',
                            key: 'dia',
                            render: (fecha: string) => dayjs(fecha).format('DD/MM/YYYY'),
                          },
                          {
                            title: 'Cantidad',
                            dataIndex: 'cantidad',
                            key: 'cantidad',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Subtotal',
                            dataIndex: 'subtotal',
                            key: 'subtotal',
                            align: 'right' as const,
                            render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                          },
                        ]}
                      />
                    </Card>
                  </div>
                )
              }
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default ConsumoInterno;
